/**
 * @file Processor, the base processor for the grids to use
 * @author Wim Haanstra
 */
import * as moment from 'moment';
import { Timeslot } from '../helpers/timeslot';
import { Momentos } from '../../base/helpers/momentos';
import { KeyedCollection } from '../../base/helpers/remove-keyed-collection';
import { AgendaAssignment } from '../helpers/agenda-assignment';
import { AgendaAppointment } from '../helpers/agenda-appointment';
import { AgendaAvailability } from '../helpers/agenda-availability';
import { AgendaWorkPattern } from '../helpers/agenda-workpattern';
import { EntityState } from 'breeze-client';
import { AgendaAttendant } from '../helpers/agenda-attendant';
/**
 * Base class for the processors needed for the grid
 * This has a collection of functions that can be overriden to fill the grid
 * in the right way.
 *
 * @export
 * @class Processor
 */
var Processor = /** @class */ (function () {
    function Processor() {
        /** Aantal stappen die gebruikt worden om door de tijdperiode te lopen */
        this.spanStep = 1;
        /** Stapformaat die gebruikt wordt om door de tijdperiode te loopen */
        this.span = 'hours';
        /** Formaat wat gebruikt wordt voor afronding */
        this.endSpan = 'hours';
        /** Collectie van timeslots gekoppeld aan een bepaalde key. */
        this.keyedTimeslots = new KeyedCollection();
        /** The start date used in the query */
        this.queryStartDate = moment();
        /** The finish date used in the query */
        this.queryFinishDate = moment();
    }
    /**
     * Method to determing the Z-index for the supplied items, based on length
     * and start time of the item.
     *
     * @static
     * @param {PlanItemWrapper[]} items Items that should processed
     * @returns {PlanItemWrapper[]} The items with new Z-indexes and orders
     */
    Processor.orderItems = function (items) {
        items = items.sort(function (lhs, rhs) {
            var diff = lhs.timeSpan - rhs.timeSpan;
            if (diff === 0) { // Same length!
                return lhs.start.isSameOrBefore(rhs.start) ? -1 : 1;
            }
            else if (diff > 0) {
                return -1;
            }
            else {
                return 1;
            }
        });
        items.forEach(function (item) {
            item.zIndex = 0;
        });
        var processed = [];
        items.forEach(function (item) {
            // Availabilities worden uitgesloten, deze mogen de Z-index niet beinvloeden.
            if (!(item instanceof AgendaAvailability || item instanceof AgendaWorkPattern)) {
                var itemRange_1 = new Momentos(item.start, item.finish);
                var overlaps = processed.filter(function (filterItem) {
                    return itemRange_1.intersectsWith(new Momentos(filterItem.start, filterItem.finish));
                });
                overlaps.sort(function (lhs, rhs) { return lhs.zIndex - rhs.zIndex; });
                if (overlaps.length > 0) {
                    var lastItem = overlaps[overlaps.length - 1];
                    item.zIndex = lastItem.zIndex + 1;
                }
                var covering = processed.filter(function (filterItem) {
                    return item.start.isSame(filterItem.start) && item.finish.isSame(filterItem.finish);
                });
                item.covers = covering.length;
                processed.push(item);
            }
        });
        items = items.sort(function (lhs, rhs) { return lhs.zIndex - rhs.zIndex; });
        return items;
    };
    /**
     * Add a timeslot to the processor, this also checks if a timeslot already exists.
     * When a timeslot already exists, the existing one is returned
     *
     * @param {string} key Key for the collection the timeslot should be inserted in
     * @param {moment.Moment} startDate The start date/time for the timeslot
     * @param {moment.Moment} endDate The end date/time for the timeslot
     * @returns {Timeslot} A generated timeslot, or an existing timeslot when one already exists.
     */
    Processor.prototype.addTimeslot = function (key, startDate, endDate) {
        var timeslots = this.getTimeslots(key);
        // At this moment I think there is never a previous added timeslot with
        // the same start and end. Each add timeslot is unique even over the resources
        // in the planboard. Because of that the following lines are disabled.
        // const existingTimeslots = timeslots.filter(timeslot => {
        //     return (timeslot.startDate.isSame(startDate) && timeslot.endDate.isSame(endDate));
        // });
        // if (existingTimeslots.length === 0) {
        var timeslot = new Timeslot();
        timeslot.startDate = startDate.clone();
        timeslot.endDate = endDate.clone();
        timeslots.push(timeslot);
        this.keyedTimeslots.Add(key, timeslots);
        return timeslot;
        // } else {
        //     console.log('timeslot found!');
        //     return existingTimeslots[0];
        // }
    };
    /**
     * Clear all timeslots from the processor, except plugin timeslots.
     */
    Processor.prototype.clearTimeslots = function () {
        var _this = this;
        this.keyedTimeslots.Keys.forEach(function (key) {
            if (key.indexOf(Processor.pluginKey) === 0) {
                return;
            }
            var timeslots = _this.keyedTimeslots.Item(key);
            timeslots.length = 0;
        });
    };
    /**
     * Counting the currently in use time slots
     */
    Processor.prototype.countTimeslotsInUse = function () {
        var count = 0;
        this.keyedTimeslots.Values.forEach(function (timeslots) {
            count = count + timeslots.length;
        });
        return count;
    };
    /**
     * Methode om tijdsloten te vinden die bij bijbehorende start en eindtijden horen
     * Er kunnen meerdere tijdsloten worden gevonden, als een object langer duurt dan
     * de lengte van een tijdslot.
     *
     * De key wordt gebruikt om een tijdslot te vinden die aan een bepaalde key is toegewezen.
     */
    Processor.prototype.findTimeSlots = function (key, startDate, endDate) {
        var timeslots = this.getTimeslots(key);
        var range = new Momentos(startDate, endDate);
        var results = timeslots.filter(function (timeslot) {
            return range.intersectsWith(new Momentos(timeslot.startDate, timeslot.endDate));
        });
        return results;
    };
    /**
     * Refresh de huidige tijdsloten met objecten die nieuw zijn.
     * Hierdoor hoeven tijdsloten niet opnieuw aangemaakt te worden en kan de inhoud
     * worden vervangen
     */
    Processor.prototype.refreshWith = function (key, entities) {
        var _this = this;
        var timeslots = this.getTimeslots(key);
        timeslots.forEach(function (timeslot) {
            timeslot.clear();
        });
        var result = entities
            .filter(function (item) {
            var state = item.entityAspect.entityState;
            if (state === EntityState.Detached || state === EntityState.Deleted) {
                return false;
            }
            return true;
        })
            .map(function (value) {
            var item = value;
            switch (item.ItemClass.toLowerCase()) {
                case 'assignment':
                    return new AgendaAssignment(value);
                case 'appointment':
                    return new AgendaAppointment(value);
                case 'availability':
                    return new AgendaAvailability(value);
                case 'service':
                case 'attendant':
                    return new AgendaAttendant(value);
                case 'workpattern':
                    return new AgendaWorkPattern(value);
                default:
                    throw new Error('Unknown ItemClass ' + item.ItemClass);
            }
        });
        var results = Processor.orderItems(result);
        results.forEach(function (entity) {
            var foundTimeslots = _this.findTimeSlots(key, entity.start, entity.finish);
            entity.submitToTimeslots(foundTimeslots, _this.adjustEntity, (key === 'AGENDA'));
        });
    };
    /**
     * Used for small modifications of the entity
     *
     * @param {*} entity The entity
     * @param {boolean} isAgenda If view is agenda or not (=planboard or plugin)
     * @returns The entity
     */
    Processor.prototype.adjustEntity = function (entity, isAgenda) {
        return entity;
    };
    /**
     * Helper methode die een tijd als string accepteerd, bv '17:21' en deze
     * omzet naar een ander meegeleverd formaat
     */
    Processor.prototype.formatTime = function (time, format) {
        var date = moment(time, 'HH:mm');
        return date.format(format);
    };
    /**
     * Haal tijdsloten op die gekoppeld zijn aan een bepaalde key
     */
    Processor.prototype.getTimeslots = function (key) {
        if (!this.keyedTimeslots.ContainsKey(key)) {
            var timeslots = [];
            this.keyedTimeslots.Add(key, timeslots);
        }
        return this.keyedTimeslots.Item(key);
    };
    /**
     * Creating a grid segments data structure for fast rendering of the grid
     *
     * @param {string} label
     * @param {(Entity | {})} resource
     * @param {DetailLevel} detailLevel
     * @param {moment.Moment} date
     * @param {GridSegment[]} children
     * @param {TimeslotDecorations} timeslotDecorations
     * @returns {GridSegments}
     * @memberof Processor
     */
    Processor.prototype.createGridSegments = function (label, resource, click, children, timeslotDecorations) {
        return {
            meta: {
                label: label,
                resource: resource
            },
            click: click,
            children: children,
            gridDots: timeslotDecorations.getDots(),
            gridSplitters: timeslotDecorations.getSplitters()
        };
    };
    /**
     * Creating a grid segment data structure for fast rendering of the grid
     *
     * @param {string} label The label of the grid segment
     * @param {GridRows[]} children The children of the grid segment
     * @param {GridLabels} gridLabels The grid labels of the grid segment
     * @returns {GridSegment}
     */
    Processor.prototype.createGridSegment = function (label, children, gridLabels) {
        return {
            meta: {
                label: label
            },
            children: children,
            gridLabels: gridLabels
        };
    };
    /**
     * Creating a grid rows data structure for fast rendering of the grid
     *
     * @param {string} label The label of the grid rows
     * @param {moment.Moment} start The start date
     * @param {moment.Moment} finish The finish datae
     * @param {(Entity | {})} resource The related resource
     * @param {HorizonState} click Data for handling of the grid clicks
     * @param {GridColumn[]} children The children of the grid rows
     * @returns {GridRows}
     */
    Processor.prototype.createGridRows = function (label, start, finish, resource, click, children) {
        return {
            meta: {
                label: label,
                start: start,
                finish: finish,
                resource: resource
            },
            click: click,
            children: children
        };
    };
    /**
     * Creating a grid column data structure for fast rendering of the grid
     *
     * @param {string} label The label of the grid column
     * @param {moment.Moment} start The start date
     * @param {moment.Moment} finish The finish daet
     * @param {(Entity | {})} resource The related resource
     * @param {HorizonState} click Data for handling of the grid clicks
     * @param {Timeslot} timeslot The connected timeslot
     * @returns {GridColumn}
     */
    Processor.prototype.createGridColumn = function (label, start, finish, resource, click, timeslot) {
        return {
            meta: {
                label: label,
                start: start,
                finish: finish,
                resource: resource
            },
            click: click,
            gridAvailabilities: timeslot.gridAvailabilities,
            gridAssignments: timeslot.gridAssignments,
            gridAppointments: timeslot.gridAppointments,
            gridAbsences: timeslot.gridAbsences,
            gridAttendants: timeslot.gridAttendants,
            gridWorkPatterns: timeslot.gridWorkPatterns
        };
    };
    /**
     * This finds the last day in the range, to make sure ranges can be displayed correctly
     *
     * @param {moment} strt
     * @param {moment} finish
     * @returns {moment}
     */
    Processor.prototype.lastDayInPeriod = function (strt, finish, visibleDays) {
        var range = new Momentos(moment(strt), moment(finish), 1, 'day', 'day');
        var lastDay = null;
        var iteratorResult = range.next();
        while (!iteratorResult.done) {
            var date = iteratorResult.value;
            if (visibleDays.indexOf(date.day()) > -1) {
                lastDay = moment(date);
            }
            iteratorResult = range.next();
        }
        return lastDay;
    };
    /** The key of the plugin timeslots */
    Processor.pluginKey = 'plugin';
    return Processor;
}());
export { Processor };
